/**
 * QUANTO O PEIXE CRESCE POR DIA — e o que essa conta NÃO é.
 *
 * A curva veio dos 89 trechos medidos da própria fazenda entre 2018 e 2026:
 * os 77 ciclos de engorda mais 12 de berçário, que só apareceram quando
 * alguém leu os comentários e as cores das células da planilha mensal.
 *
 *     ganho diário (g/dia) = a × peso^(2/3) − b × peso
 *
 * É a forma de von Bertalanffy: o ganho é proporcional à SUPERFÍCIE do corpo,
 * por onde entra o alimento, menos o custo de MANTER a massa que já existe.
 * Venceu três outras formas testadas, e venceu porque é a única que reproduz
 * berçário e engorda ao mesmo tempo.
 *
 * `a` e `b` NÃO ESTÃO AQUI. Vêm da tabela `parametros`, pelo servidor, porque
 * outra piscicultura tem outro peixe, outro clima e outra curva.
 *
 * O QUE ELA NÃO É — e isto é a regra 1 do projeto:
 *
 * Ela não afirma quanto o peixe pesa. Ela estima, com 34% de erro mediano no
 * peso final, o que aconteceu ENTRE duas pesagens. No instante em que a
 * balança fala, a estimativa cala: feita a biometria, daquela data em diante o
 * peso é o medido.
 *
 * E ela só é calibrada por peso MEDIDO. A estimativa diária nunca volta para
 * dentro da curva — senão a curva passa a se dar razão sozinha, e uma curva
 * otimista mandaria mais ração, que "confirmaria" mais ganho, sem que nenhum
 * peixe tivesse sido pesado.
 */

import { dias, g, type Dias, type Gramas } from './unidades'

export interface Curva {
  /** Coeficiente do ganho, sobre peso^(2/3). */
  a: number
  /** Custo de manter a massa, sobre o peso. */
  b: number
  /**
   * Multiplica o ganho. Começa em 1,00 porque a curva JÁ é o histórico desta
   * fazenda. Vira o placar da gestão: 1,17 é o melhor quartil dos oito anos.
   */
  fator: number
}

/** Quanto este peixe ganha hoje, com a água perfeita. */
export function ganhoDiario(peso: Gramas, curva: Curva): Gramas {
  const bruto = curva.a * Math.pow(peso, 2 / 3) - curva.b * peso
  return g(Math.max(0, bruto * curva.fator))
}

/**
 * Quanto ele ganha HOJE, com a água que há hoje.
 *
 * Regra do gestor: «a % de redução do trato reflete igualmente na % de ganho
 * diário». Trato a 50%, ganho a 50%. Auto-consistente, e não inventa peixe —
 * dez dias sem tratar são dez dias sem crescer.
 *
 * RESSALVA CONHECIDA E SEM SOLUÇÃO HOJE: parte da ração vai para manutenção
 * antes de virar ganho. Em trato muito reduzido o ganho cai MAIS que
 * proporcionalmente, e esta conta linear passa a superestimar o peixe — que é
 * o lado errado de errar, porque faz o app sugerir mais ração. Falta dado para
 * corrigir, e inventar um número seria pior.
 */
export function ganhoDoDia(peso: Gramas, curva: Curva, coeficiente: number): Gramas {
  return g(ganhoDiario(peso, curva) * coeficiente)
}

/**
 * O peso estimado depois de N dias, integrando dia a dia.
 *
 * Integra em vez de multiplicar porque o ganho depende do peso, e o peso muda
 * todo dia: um peixe de 700 g ganha 7,2 g/dia, e o mesmo peixe dois meses
 * depois ganha 8,5. Multiplicar o ganho inicial pelos dias erraria para menos
 * justamente onde há mais biomassa.
 */
export function pesoApos(
  pesoInicial: Gramas,
  quantosDias: number,
  curva: Curva,
  coeficientePorDia: (dia: number) => number = () => 1,
): Gramas {
  let peso = pesoInicial as number
  for (let dia = 0; dia < Math.floor(quantosDias); dia += 1) {
    peso += ganhoDoDia(g(peso), curva, coeficientePorDia(dia))
  }
  return g(peso)
}

/**
 * Quantos dias para ir de um peso a outro, com a água perfeita.
 *
 * O passo é de uma hora, não de um dia: com passo diário a integração
 * superestima, porque usa o ganho do começo do dia durante o dia inteiro.
 * Em 400 dias a diferença passa de uma semana.
 */
export function diasEntre(de: Gramas, ate: Gramas, curva: Curva): Dias {
  if (ate <= de) return dias(0)
  const passo = 1 / 24
  let peso = de as number
  let contados = 0
  // Teto de segurança: 3.000 dias. Curva mal parametrizada não trava a tela.
  while (peso < ate && contados < 3000) {
    const ganho = ganhoDiario(g(peso), curva)
    if (ganho <= 0) return dias(Infinity)
    peso += ganho * passo
    contados += passo
  }
  return dias(contados)
}
